import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { API } from "@/App";
import { toast } from "sonner";
import { Calendar, CheckCircle2, Send } from "lucide-react";

const DiscoveryForm = () => {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", organization: "",
    title: "", level: "", message: ""
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        message: `[Discovery Call Request]\nOrganization: ${form.organization}\nTitle: ${form.title}\nLevel: ${form.level}\n\n${form.message}`,
        service_interest: "Discovery Call"
      });
      setSubmitted(true);
    } catch (err) {
      toast.error("Failed to submit. Please try again.");
    }
    setLoading(false);
  };

  if (submitted) {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="h-8 w-8 text-emerald-600" />
        </div>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">Request Received!</h3>
        <p className="text-slate-600">Thank you for your interest. Steve will be in touch within 24-48 hours to schedule your discovery call.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
          <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required data-testid="discovery-name" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Phone *</label>
          <Input type="tel" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} placeholder="(555) 123-4567" required data-testid="discovery-phone" />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
        <Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} required data-testid="discovery-email" />
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Current Organization *</label>
        <Input value={form.organization} onChange={(e) => setForm({...form, organization: e.target.value})} required data-testid="discovery-org" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Your Title *</label>
          <Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} required data-testid="discovery-title" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Level *</label>
          <Select value={form.level} onValueChange={(v) => setForm({...form, level: v})}>
            <SelectTrigger data-testid="discovery-level">
              <SelectValue placeholder="Select level" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Individual Contributor">Individual Contributor</SelectItem>
              <SelectItem value="Team Lead">Team Lead</SelectItem>
              <SelectItem value="Manager">Manager</SelectItem>
              <SelectItem value="Senior Manager">Senior Manager</SelectItem>
              <SelectItem value="Director">Director</SelectItem>
              <SelectItem value="VP">VP</SelectItem>
              <SelectItem value="SVP">SVP</SelectItem>
              <SelectItem value="C-Suite">C-Suite</SelectItem>
              <SelectItem value="Other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">What are you hoping to achieve through coaching?</label>
        <Textarea value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} rows={3} placeholder="Share your goals, challenges, or what brought you here..." data-testid="discovery-message" />
      </div>
      <Button type="submit" className="w-full bg-orange-600 hover:bg-orange-500 h-12" disabled={loading} data-testid="discovery-submit">
        <Send className="h-4 w-4 mr-2" />
        {loading ? "Submitting..." : "Request Discovery Call"}
      </Button>
    </form>
  );
};

const SessionRequestForm = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        message: `[Coaching Session Request - Existing Client]\n\n${form.message}`,
        service_interest: "Coaching Session"
      });
      setSubmitted(true);
    } catch (err) {
      toast.error("Failed to submit. Please try again.");
    }
    setLoading(false);
  };

  if (submitted) {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="h-8 w-8 text-emerald-600" />
        </div>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">Request Received!</h3>
        <p className="text-slate-600">Steve will reach out shortly to schedule your next session.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
        <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required data-testid="session-name" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
          <Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} required data-testid="session-email" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
          <Input type="tel" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} data-testid="session-phone" />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Message</label>
        <Textarea value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} rows={3} placeholder="Any topics or areas you'd like to focus on in your next session..." data-testid="session-message" />
      </div>
      <Button type="submit" variant="outline" className="w-full h-12" disabled={loading} data-testid="session-submit">
        <Send className="h-4 w-4 mr-2" />
        {loading ? "Submitting..." : "Request Another Session"}
      </Button>
    </form>
  );
};

const BookingPage = () => {
  return (
    <div className="min-h-screen bg-stone-50 pt-20" data-testid="booking-page">
      <div className="max-w-5xl mx-auto px-6 py-8">

        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Calendar className="h-8 w-8 text-orange-600" />
          </div>
          <h1 className="text-3xl md:text-4xl font-serif font-medium text-slate-900 mb-4">
            Connect with Steve
          </h1>
          <p className="text-lg text-slate-600">
            Request a complimentary discovery call or schedule your next coaching session. 
            I look forward to connecting with you!
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Discovery Call */}
          <Card className="border-2 border-orange-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-3">
                <span className="text-2xl">🏔️</span>
                Discovery Call
              </CardTitle>
              <p className="text-sm text-slate-500">
                A free 30-minute call to explore your leadership goals and discuss 
                how coaching can support your journey.
              </p>
            </CardHeader>
            <CardContent>
              <DiscoveryForm />
            </CardContent>
          </Card>

          {/* Coaching Session Request */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-3">
                <span className="text-2xl">⛰️</span>
                Request Another Session
              </CardTitle>
              <p className="text-sm text-slate-500">
                For current coaching clients — request your next session and Steve will reach out to schedule.
              </p>
            </CardHeader>
            <CardContent>
              <SessionRequestForm />
            </CardContent>
          </Card>
        </div>

        {/* Contact Alternative */}
        <div className="text-center mt-12">
          <p className="text-slate-600 mb-4">
            Have a question first?
          </p>
          <Link to="/#contact">
            <Button variant="outline">
              Send a General Message
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default BookingPage;
